import React from 'react';
import NextLink from 'next/link';
import { hexToRgba } from '@mrkt/features/color-conversion';
import styled, { css } from 'styled-components';
import { white } from '@spotify-internal/encore-foundation';
import { eventHandler, eventHandlerWithAction } from '../Tracking';

type LinkProps = {
  children: React.ReactNode;
  track: [string, string, string?]; // category/label for GA tracking - category ignored for external
  href: string;
  prefetch?: boolean; // same as NextJS link
  external?: boolean; // external link opens new window, etc.
  component?: any; // underlying <a> component, mostly for use with styled-components
  highlight?: boolean;
  shallow?: boolean; // same as NextJS link
};

type Props = { [key: string]: any };

const defaultComponent = styled.a``;

/**
 * Wraps NextJS link, adds:
 * - track: GA tracking (required, auto for external)
 * - external: opens external links in new tab, etc.
 * - adds 'a' component with default link styles, configurable
 */
export const UnstyledLink: React.FC<LinkProps> = (props: LinkProps) => {
  const {
    children,
    track,
    href,
    external = false,
    prefetch = true,
    component: AComponent = defaultComponent,
    ...extraProps
  } = props;

  const linkProps: Props = extraProps;
  const aProps: Props = {};

  if (track) {
    let [trackCategory, trackLabel, trackAction] = track;
    if (external) {
      trackCategory = trackCategory || 'External Link';
      trackLabel = trackLabel || linkProps.href;
      trackAction = trackAction || 'click';
    }
    if (trackCategory && trackLabel && trackAction) {
      aProps.onClick = eventHandlerWithAction(
        `Frodor - ${trackCategory}`,
        trackAction,
        trackLabel,
      );
    } else if (trackCategory && trackLabel) {
      aProps.onClick = eventHandler(`Frodor - ${trackCategory}`, trackLabel);
    }
  }

  if (external) {
    aProps.target = '_blank';
    aProps.rel = 'noopener noreferrer';
  }

  if (linkProps.highlight) {
    aProps.className = 'highlight';
  }
  delete linkProps.highlight;

  if (
    prefetch === false ||
    external ||
    (href &&
      (href.startsWith('http') ||
        href.startsWith('//') ||
        href === '/c' ||
        href.startsWith('/c/')))
  ) {
    linkProps.prefetch = false;
  }

  aProps.className = `${aProps.className || ''} ${linkProps.className || ''}`;
  delete linkProps.className;

  // pass along all data & aria props to href
  for (const key of Object.keys(linkProps).filter(
    k => k.startsWith('aria-') || k.startsWith('data-'),
  )) {
    aProps[key] = linkProps[key];
    delete linkProps[key];
  }

  if (external) {
    return (
      <AComponent {...aProps} href={href}>
        {children}
      </AComponent>
    );
  }

  return (
    <NextLink {...linkProps} href={href} passHref>
      <AComponent {...aProps}>{children}</AComponent>
    </NextLink>
  );
};

export const getLinkStyle = (props: any) => css`
  text-decoration: none;
  color: ${hexToRgba(
    props.customTextColor || props.theme.link?.color || white,
    0.75,
  )};

  &:hover,
  &:focus:not(:disabled),
  &:active:not(:disabled),
  &.highlight {
    text-decoration: none;
    color: ${props.customTextColor || props.theme.link?.color || white};
  }

  &:focus:not(:disabled) {
    outline: thin dotted black;
  }
`;

export const Link = styled(UnstyledLink)<LinkProps>`
  ${props => getLinkStyle(props)}
`;
